import React, { useState } from 'react';
import { VStack, HStack, Text, Button } from '@chakra-ui/react';
import Drawer, { DrawerHeader, DrawerTitle, DrawerBody, DrawerFooter, DrawerCloseTrigger } from '@/components/ui/Drawer';
import withHashDrawer from '@/components/ui/withHashDrawer';
import { useGetUserByIdQuery, useSendForgotPasswordMutation } from '../api';

interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityId?: number;
  onSuccess?: () => void;
}

const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({
  isOpen,
  onClose,
  entityId,
  onSuccess,
}) => {
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const { data: user } = useGetUserByIdQuery(entityId!, {
    skip: !entityId,
  });
  
  const [sendForgotPassword, { isLoading }] = useSendForgotPasswordMutation();

  const handleSubmit = async () => {
    if (!user) return;
    
    setError('');
    setSuccess(false);

    try {
      await sendForgotPassword({
        email: user.email,
      }).unwrap();
      
      setSuccess(true);
      onSuccess?.();
    } catch (err: any) {
      setError(err.data?.message || 'Failed to send password reset email');
    }
  };

  const handleClose = () => {
    setError('');
    setSuccess(false);
    onClose();
  };

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} size="md">
      <DrawerHeader>
        <DrawerTitle>Send Password Reset</DrawerTitle>
        <DrawerCloseTrigger />
      </DrawerHeader>
      
      <DrawerBody>
        <VStack gap={4} align="stretch">
          {user && (
            <Text fontSize="sm" color="gray.600">
              Send password reset email to: <strong>{user.first_name} {user.last_name}</strong>
            </Text>
          )}
          
          <div>
            <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={1}>
              Email Address
            </Text>
            <Text>{user?.email}</Text>
          </div>
          
          {success && (
            <Text color="green.500" fontSize="sm">
              Password reset email has been sent successfully.
            </Text>
          )}
          
          {error && (
            <Text color="red.500" fontSize="sm">
              {error}
            </Text>
          )}
          
          {!success && (
            <Text fontSize="sm" color="gray.600">
              The user will receive an email with instructions to reset their password.
            </Text>
          )}
        </VStack>
      </DrawerBody>
      
      <DrawerFooter>
        <HStack gap={3}>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            {success ? 'Close' : 'Cancel'}
          </Button>
          {!success && (
            <Button
              onClick={handleSubmit}
              loading={isLoading}
              disabled={isLoading}
              colorPalette="orange"
            >
              Send Reset Email
            </Button>
          )}
        </HStack>
      </DrawerFooter>
    </Drawer>
  );
};

export default withHashDrawer(ForgotPasswordModal, {
  action: 'forgot-password',
});
